import { compose, entries, get, reduce, toNumber } from 'lodash/fp';
import { ApplicationState, CommanderThunkAction, CustomLink } from '@mews-commander/types';
import { store } from '@mews-commander/legacy-app-instance';
import { openInBrowserTab } from '@mews-commander/utils';
import {
    getCurrentEnterpriseCustomLinks as getCurrentEnterpriseCustomLinksJS,
    getCurrentEnterpriseUserPreferredServiceId,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { trackEvent } from '../../middleware/createAnalyticsMiddleware';
import {
    navigateToHome,
    navigateToNewCompanyScreen,
    navigateToNewCustomerScreen,
    navigateToNRS,
    navigateToReservationOverview,
    navigateToResourceStatusReport,
    navigateToTimeline,
} from './actions';
import { CUSTOM_LINK_KEYS, ShortcutKey, SHORTCUTS_EVENT_CATEGORY } from './constants';

type getCurrentEnterpriseCustomLinks = (state: ApplicationState) => CustomLink[];
const getCurrentEnterpriseCustomLinks = getCurrentEnterpriseCustomLinksJS as getCurrentEnterpriseCustomLinks;

const getCustomLinkUrlOnIndex = (index: number) => (array: CustomLink[]) => get<CustomLink, 'url'>('url')(array[index]);

const navigateToCustomLinkOnIndex =
    (index: number): CommanderThunkAction =>
    (_dispatch, getState) => {
        const customLinks = getCurrentEnterpriseCustomLinks(getState());

        const url = getCustomLinkUrlOnIndex(index)(customLinks);
        if (url) {
            openInBrowserTab(url);
        }
    };

const ALT_MODIFIER = {
    modifiers: {
        altKey: true,
    },
};

const trackShortcutEvent = (key: ShortcutKey) =>
    trackEvent({
        eventCategory: SHORTCUTS_EVENT_CATEGORY,
        eventAction: 'used',
        eventLabel: key,
        eventValue: key,
    });

const onShortcutEvent =
    <T>(key: ShortcutKey, handler: (arg: T) => CommanderThunkAction, arg: T) =>
    () => {
        const { activeElement } = document;
        if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
            return;
        }
        store().dispatch(trackShortcutEvent(key));
        store().dispatch(handler(arg));
    };

export interface Shortcut {
    modifiers: {
        altKey?: boolean;
        ctrlKey?: boolean;
    };
    onEvent: () => void;
}

const createCustomLinkShortcuts = <T extends ShortcutKey>(data: readonly T[]) =>
    compose(
        reduce<[string, T], Record<T, Shortcut>>(
            (acc, [index, value]) => ({
                ...acc,
                [value]: {
                    ...ALT_MODIFIER,
                    onEvent: () => {
                        store().dispatch(trackShortcutEvent(value));
                        store().dispatch(navigateToCustomLinkOnIndex(toNumber(index)));
                    },
                },
            }),
            {} as Record<T, Shortcut>
        ),
        entries
    )(data);

export const Shortcuts = {
    [ShortcutKey.TimeLine]: {
        ...ALT_MODIFIER,
        onEvent: () => {
            const serviceId = getCurrentEnterpriseUserPreferredServiceId(store().getState());
            return onShortcutEvent(ShortcutKey.TimeLine, navigateToTimeline, { serviceId })();
        },
    },
    [ShortcutKey.ReservationOverview]: {
        ...ALT_MODIFIER,
        onEvent: onShortcutEvent(ShortcutKey.ReservationOverview, navigateToReservationOverview, {}),
    },
    [ShortcutKey.Dashboard]: {
        ...ALT_MODIFIER,
        onEvent: onShortcutEvent(ShortcutKey.Dashboard, navigateToHome, {}),
    },
    [ShortcutKey.NewReservationScreen]: {
        ...ALT_MODIFIER,
        onEvent: onShortcutEvent(ShortcutKey.NewReservationScreen, navigateToNRS, {}),
    },
    [ShortcutKey.NewCustomerScreen]: {
        ...ALT_MODIFIER,
        onEvent: onShortcutEvent(ShortcutKey.NewCustomerScreen, navigateToNewCustomerScreen, {}),
    },
    [ShortcutKey.NewCompanyScreen]: {
        ...ALT_MODIFIER,
        onEvent: onShortcutEvent(ShortcutKey.NewCompanyScreen, navigateToNewCompanyScreen, {}),
    },
    [ShortcutKey.ResourceStatusReport]: {
        ...ALT_MODIFIER,
        onEvent: onShortcutEvent(ShortcutKey.ResourceStatusReport, navigateToResourceStatusReport, {}),
    },
    ...createCustomLinkShortcuts(CUSTOM_LINK_KEYS),
};
